import { createPinia, type StateTree } from 'pinia'
import { ViteSSG, type ViteSSGContext } from 'vite-ssg'

import App from './App.vue'
import { startDeferredClientModules } from '@/bootstrap/deferredClientModules'
import { useTracking } from '@/composables/useTracking'
import { i18n } from '@/plugins/i18n'
import { installRouteLocaleSync, routerOptions } from '@/router'
import { loadSsgContent, resolveSsgRouteLocale } from '@/ssg/content'
import { SSG_HEAD_PROPERTY } from '@/ssg/head'
import { createSsgRouteSnapshot, resolveSsgRouteTarget } from '@/ssg/routeSnapshot'
import { discoverSsgRoutes } from '@/ssg/routes'
import { transformSsgState } from '@/ssg/state'
import { useContentStore } from '@/stores/content.store'
import { useFrontpageStore } from '@/stores/frontpage.store'
import { useHeaderNavigationStore, createHeaderNavigationSnapshot } from '@/stores/headerNavigation.store'
import { useHeroStore } from '@/stores/hero.store'
import { useLocaleStore } from '@/stores/locale.store'
import { applyThemeColorProfileOverride } from '@/theme/colorProfiles'
import { installTrustedJsonLdHeadPlugin } from '@/utils/trustedJsonLdScript'

import '../assets/css/fonts.css'
import '../assets/css/style.css'

applyThemeColorProfileOverride()

function isStateTree(value: unknown): value is StateTree {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

async function installUnheadV3(context: ViteSSGContext<true>) {
  const { app } = context
  const head = import.meta.env.SSR
    ? (await import('@unhead/vue/server')).createHead()
    : (await import('@unhead/vue/client')).createHead()

  if (!import.meta.env.SSR) {
    installTrustedJsonLdHeadPlugin(head)
  }

  app.use(head)
  app.config.globalProperties[SSG_HEAD_PROPERTY] = head
}

export const createApp = ViteSSG(
  App,
  routerOptions,
  async (context) => {
    const { app, initialState, routePath, router } = context
    const pinia = createPinia()

    await installUnheadV3(context)

    app.use(i18n)
    app.use(pinia)
    installRouteLocaleSync(router)

    if (import.meta.env.SSR) {
      if (routePath) {
        const locale = resolveSsgRouteLocale(routePath)
        const localeStore = useLocaleStore(pinia)
        localeStore.setLocale(locale)

        const catalog = await loadSsgContent(locale)
        const routeSnapshot = createSsgRouteSnapshot(routePath, catalog)
        const routeTarget = resolveSsgRouteTarget(routePath, catalog)
        useContentStore(pinia).hydrate(routeSnapshot)
        useFrontpageStore(pinia).hydrate(catalog.frontpage, catalog.frontpageEtag)
        useHeaderNavigationStore(pinia).hydrate(createHeaderNavigationSnapshot(
          catalog.gyms,
          catalog.trainers,
          catalog.etags,
          locale,
        ))

        const hero = routeTarget
          ? catalog[routeTarget.resource].find((item) => item.id === routeTarget.id) ?? null
          : null
        if (hero) {
          useHeroStore(pinia).setHeroFromSegment(hero)
        }

        initialState.pinia = pinia.state.value
      }
    } else {
      if (isStateTree(initialState.pinia)) {
        pinia.state.value = initialState.pinia

        const contentStore = useContentStore(pinia)
        contentStore.hydrate({
          pages: contentStore.pages,
          posts: contentStore.posts,
          attachments: contentStore.attachments,
          taxonomies: contentStore.taxonomies,
          membership: contentStore.membership,
          trainers: contentStore.trainers,
          gyms: contentStore.gyms,
          etags: contentStore.etags,
          coverage: contentStore.coverage,
          routeTarget: contentStore.routeTarget,
        })

        const frontpageStore = useFrontpageStore(pinia)
        if (frontpageStore.frontpage) {
          frontpageStore.hydrate(frontpageStore.frontpage, frontpageStore.etag)
        }
      }

      useLocaleStore(pinia).initLocale()

      void router.isReady().then(() => {
        useTracking()
        startDeferredClientModules(app)
      })
    }
  },
  {
    hydration: true,
    transformState: transformSsgState,
    useHead: false,
  },
)

export async function includedRoutes(): Promise<string[]> {
  return discoverSsgRoutes()
}
